import type {OmitStrict} from 'type-zoo'
import type {
  TMinorActionPromptDataPayRent,
  TPortfolioNeighborhoodId,
} from '@kapitalist/contract/types'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import {MillionsMath} from '@kapitalist/contract/utils/millions/math'
import {skins} from '~/game/skins'
import type GamePlayer from '~/models/game/GamePlayer'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type MajorActionRentBase from '~/models/game/MajorActions/MajorActionRentBase'
import type {
  TMinorActionPromptUserFacingDescriptionData,
  TMinorActionPromptValidResponseData,
} from '~/models/game/MinorActionPrompt'
import MinorActionPromptPaySomething from '~/models/game/MinorActionPrompts/MinorActionPromptPaySomething'
import MinorActionPayRent from '~/models/game/MinorActions/MinorActionPayRent'
import type {AnyMinorAction} from '~/models/game/types'
import {sentenceCase, xVerbY} from '~/utils/language'

type PublicMembersFromApi = OmitStrict<TMinorActionPromptDataPayRent, 'type'>

export default class MinorActionPromptPayRent
  extends MinorActionPromptPaySomething<TMinorActionPromptDataPayRent>
  implements PublicMembersFromApi
{
  portfolioNeighborhoodId: TPortfolioNeighborhoodId // what neighborhood the rent we're being prompted to pay was charged for
  majorAction: MajorActionRentBase

  constructor(
    data: TMinorActionPromptDataPayRent,
    majorAction: MajorActionRentBase,
    majorActor: GamePlayer,
    minorActor: GamePlayer
  ) {
    super(data, majorActor, minorActor)
    this.portfolioNeighborhoodId = data.portfolioNeighborhoodId
    this.majorAction = majorAction
  }

  protected _validResponses(
    data: TMinorActionPromptValidResponseData
  ): AnyMinorAction | AnyMinorAction[] {
    return this.withJustSayNo(
      data,
      new MinorActionPayRent({
        ...this.datasource,
        paidWith: [], // this will be filled in later once the user chooses how to pay
      })
    )
  }

  public userFacingDescription(
    data: TMinorActionPromptUserFacingDescriptionData
  ): string {
    const {gamePosition, tense, skin} = data
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const {majorActor, minorActor} = this

    const {conjugatedVerb, xNoun, yNoun} = xVerbY(
      tense,
      minorActor,
      'pay',
      majorActor,
      currentPlayer?.id
    )

    return skins[skin].gameLogDescriptions.minorActionPrompts.payPrompt({
      tense,
      actor: xNoun === minorActor.username ? xNoun : sentenceCase(xNoun),
      verb: conjugatedVerb,
      targetName: yNoun,
      amount: show$(
        MillionsMath.min(minorActor.portfolio.totalValue(), this.millionsOwed)
      ),
    })
  }
}
